import {
  type CalcResult,
  err,
  isNonNegativeInteger,
  isPositiveFinite,
  ok,
} from "../types.ts";
import {
  type BoxFillAwg,
  boxFillSizeRank,
  isBoxFillAwg,
  volumeAllowance,
} from "../tables/boxFill.ts";

export type BoxFillConductorGroup = { awg: BoxFillAwg; count: number };

export type BoxFillInput = {
  boxVolumeIn3: number;
  /** Insulated conductors entering/leaving the box (not equipment grounds). */
  conductors: BoxFillConductorGroup[];
  /** Number of devices/yokes (each strap = 2× largest conductor). */
  deviceCount: number;
  /** True if one or more internal cable clamps are present (counts once). */
  internalClamps: boolean;
  /** Count of support fittings (fixture studs, hickeys); each = 1× largest. */
  supportFittings: number;
  /** Equipment grounding conductors; ALL together = 1× volume of the largest. */
  groundingConductors: BoxFillConductorGroup[];
};

export type BoxFillResult = {
  requiredVolumeIn3: number;
  boxVolumeIn3: number;
  remainingVolumeIn3: number;
  overfilled: boolean;
  breakdown: {
    conductorsIn3: number;
    clampsIn3: number;
    supportFittingsIn3: number;
    devicesIn3: number;
    groundsIn3: number;
  };
};

function validateGroups(groups: BoxFillConductorGroup[], label: string): string | null {
  for (const g of groups) {
    if (!isBoxFillAwg(g.awg)) {
      return `${label}: unsupported conductor size "${String(g.awg)}" (box fill covers 18–6 AWG).`;
    }
    if (!isNonNegativeInteger(g.count)) {
      return `${label}: count must be a whole number ≥ 0.`;
    }
  }
  return null;
}

/** Largest (physically biggest) AWG across the given groups, or null if none. */
function largestAwg(...groups: BoxFillConductorGroup[][]): BoxFillAwg | null {
  let best: BoxFillAwg | null = null;
  for (const groupList of groups) {
    for (const g of groupList) {
      if (g.count <= 0) continue;
      if (best === null || boxFillSizeRank(g.awg) > boxFillSizeRank(best)) {
        best = g.awg;
      }
    }
  }
  return best;
}

/**
 * Box fill per NEC 2023, 314.16(B)(1)–(5):
 *  (1) each insulated conductor → its own volume allowance by AWG
 *  (2) one or more internal clamps → 1× volume of the largest conductor
 *  (3) each support fitting (stud/hickey) → 1× volume of the largest conductor
 *  (4) each device yoke → 2× volume of the largest conductor
 *  (5) all equipment grounding conductors → 1× volume of the largest ground
 *
 * "Largest conductor" for (2)–(4) is the biggest conductor in the box,
 * including grounds. Required volume is compared to the box's volume.
 */
export function boxFill(input: BoxFillInput): CalcResult<BoxFillResult> {
  const {
    boxVolumeIn3,
    conductors,
    deviceCount,
    internalClamps,
    supportFittings,
    groundingConductors,
  } = input;

  if (!isPositiveFinite(boxVolumeIn3)) {
    return err("Box volume (in³) must be a positive number.");
  }
  if (!Array.isArray(conductors) || !Array.isArray(groundingConductors)) {
    return err("Conductors and grounding conductors must be lists.");
  }
  const condErr = validateGroups(conductors, "Conductors");
  if (condErr) return err(condErr);
  const gndErr = validateGroups(groundingConductors, "Grounding conductors");
  if (gndErr) return err(gndErr);
  if (!isNonNegativeInteger(deviceCount)) {
    return err("Device count must be a whole number ≥ 0.");
  }
  if (typeof internalClamps !== "boolean") {
    return err("internalClamps must be true or false.");
  }
  if (!isNonNegativeInteger(supportFittings)) {
    return err("Support fitting count must be a whole number ≥ 0.");
  }

  // (1) conductor fill
  let conductorsIn3 = 0;
  for (const c of conductors) {
    conductorsIn3 += volumeAllowance(c.awg) * c.count;
  }

  const biggest = largestAwg(conductors, groundingConductors);
  const needsLargest = internalClamps || supportFittings > 0 || deviceCount > 0;
  if (needsLargest && biggest === null) {
    return err(
      "Clamps, support fittings, and devices are sized from the largest conductor — add at least one conductor.",
    );
  }
  const largestVol = biggest === null ? 0 : volumeAllowance(biggest);

  // (2) clamps — counted once if any present
  const clampsIn3 = internalClamps ? largestVol : 0;
  // (3) support fittings — each counts once
  const supportFittingsIn3 = supportFittings * largestVol;
  // (4) devices/yokes — each counts as two
  const devicesIn3 = deviceCount * 2 * largestVol;
  // (5) grounding conductors — all together count as one of the largest ground
  const largestGround = largestAwg(groundingConductors);
  const groundsIn3 = largestGround === null ? 0 : volumeAllowance(largestGround);

  const requiredVolumeIn3 =
    conductorsIn3 + clampsIn3 + supportFittingsIn3 + devicesIn3 + groundsIn3;

  return ok({
    requiredVolumeIn3,
    boxVolumeIn3,
    remainingVolumeIn3: boxVolumeIn3 - requiredVolumeIn3,
    overfilled: requiredVolumeIn3 > boxVolumeIn3,
    breakdown: {
      conductorsIn3,
      clampsIn3,
      supportFittingsIn3,
      devicesIn3,
      groundsIn3,
    },
  });
}
